import { useCallback } from "react";
import { useAdmin } from "@/context/AdminContext";
import { useDarkMode } from "@/context/DarkModeContext";
import { useAdminStorage } from "@/hooks/useAdminStorage";
import { useLogoFont } from "@/hooks/useLogoFont";
import AdminEditField from "@/components/feature/AdminEditField";
import RichText from "@/components/base/RichText";
import IconPicker from "@/components/feature/IconPicker";
import AppendableBlocks from "@/components/feature/AppendableBlocks";
import { defaultWhatsIncluded, type WhatsIncludedData, type IncludedItem } from "@/data/siteContent";

interface WhatsIncludedSectionProps { dataKey?: string; }

export default function WhatsIncludedSection({ dataKey }: WhatsIncludedSectionProps = {}) {
  const { isEditing: isAdmin } = useAdmin();
  const { isDark } = useDarkMode();
  const { fontFamily: headingFont, style: headingStyle } = useLogoFont();
  const [data, setData] = useAdminStorage<WhatsIncludedData>(dataKey ?? "ma_whats_included", defaultWhatsIncluded);

  const set = useCallback(<K extends keyof WhatsIncludedData>(key: K, val: WhatsIncludedData[K]) => {
    setData({ ...data, [key]: val });
  }, [data, setData]);

  const updateItem = useCallback((i: number, field: "title" | "body" | "icon", val: string) => {
    const items = data.items.map((item, idx) => idx === i ? { ...item, [field]: val } : item);
    setData({ ...data, items });
  }, [data, setData]);

  const addItem = useCallback(() => {
    const newItem: IncludedItem = {
      icon: "ri-star-line",
      title: "New Feature",
      body: "Click to edit this description.",
    };
    setData({ ...data, items: [...data.items, newItem] });
  }, [data, setData]);

  const removeItem = useCallback((i: number) => {
    setData({ ...data, items: data.items.filter((_, idx) => idx !== i) });
  }, [data, setData]);

  const bg = isDark ? "bg-gray-900" : "#fff9fb";
  const cardBg = isDark ? "bg-gray-800 border-gray-700" : "bg-white border-pink-100";
  const headingColor = isDark ? "text-white" : "text-gray-900";
  const subColor = isDark ? "text-gray-400" : "text-gray-500";
  const iconBg = isDark ? "bg-pink-900/40" : "bg-pink-50";

  return (
    <section id="whats-included" className={`py-24 px-6 transition-colors duration-300 ${isDark ? "bg-gray-900" : ""}`} style={isDark ? {} : { background: bg }}>
      <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            {(isAdmin || data.eyebrow) && (
              isAdmin ? (
                <AdminEditField value={data.eyebrow} onSave={(v) => set("eyebrow", v)} onDelete={() => set("eyebrow", "")} inputClassName="text-green-500 text-sm font-bold uppercase tracking-widest text-center">
                  <span className="text-green-500 text-sm font-bold uppercase tracking-widest">{data.eyebrow}</span>
                </AdminEditField>
              ) : (
                <span className="text-green-500 text-sm font-bold uppercase tracking-widest">{data.eyebrow}</span>
              )
            )}
            {(isAdmin || data.heading) && (
              isAdmin ? (
                <AdminEditField value={data.heading} onSave={(v) => set("heading", v)} onDelete={() => set("heading", "")} className="mt-3 mb-4" inputClassName="text-4xl font-extrabold text-center">
                  <h2 className={`text-4xl md:text-5xl font-extrabold mt-3 mb-4 ${headingColor}`} style={{ fontFamily: headingFont, ...headingStyle }}>{data.heading}</h2>
                </AdminEditField>
              ) : (
                <h2 className={`text-4xl md:text-5xl font-extrabold mt-3 mb-4 ${headingColor}`} style={{ fontFamily: headingFont, ...headingStyle }}>{data.heading}</h2>
              )
            )}
            {(isAdmin || data.subheading) && (
              isAdmin ? (
                <AdminEditField value={data.subheading} onSave={(v) => set("subheading", v)} onDelete={() => set("subheading", "")} multiline inputClassName="text-lg text-center">
                  <p className={`text-lg max-w-2xl mx-auto ${subColor}`}>{data.subheading}</p>
                </AdminEditField>
              ) : (
                <p className={`text-lg max-w-2xl mx-auto ${subColor}`}>{data.subheading}</p>
              )
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {data.items.map((item, i) => (
              <div key={i} className={`rounded-2xl p-6 border flex gap-4 items-start hover:-translate-y-0.5 transition-transform relative ${cardBg}`}>
                {/* Admin remove button */}
                {isAdmin && (
                  <button
                    onClick={() => removeItem(i)}
                    className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center bg-red-100 hover:bg-red-500 text-red-400 hover:text-white rounded-full cursor-pointer transition-colors z-10"
                    title="Remove this card"
                  >
                    <i className="ri-close-line text-xs"></i>
                  </button>
                )}
                <div className={`w-12 h-12 flex items-center justify-center rounded-xl flex-shrink-0 ${iconBg} relative`}>
                  {isAdmin ? (
                    <IconPicker value={item.icon} onChange={(ic) => updateItem(i, "icon", ic)} className="absolute inset-0 flex items-center justify-center" />
                  ) : (
                    <i className={`${item.icon} text-pink-500 text-2xl`}></i>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  {isAdmin ? (
                    <>
                      <AdminEditField value={item.title} onSave={(v) => updateItem(i, "title", v)} className="mb-1" inputClassName="font-bold text-base">
                        <h4 className={`font-bold text-base mb-1 ${headingColor}`}>{item.title}</h4>
                      </AdminEditField>
                      <AdminEditField value={item.body} onSave={(v) => updateItem(i, "body", v)} multiline richText inputClassName="text-sm leading-relaxed">
                        <RichText html={item.body} as="p" className={`text-sm leading-relaxed ${subColor}`} />
                      </AdminEditField>
                    </>
                  ) : (
                    <>
                      <h4 className={`font-bold text-base mb-1 ${headingColor}`}>{item.title}</h4>
                      <RichText html={item.body} as="p" className={`text-sm leading-relaxed ${subColor}`} />
                    </>
                  )}
                </div>
              </div>
            ))}

            {/* Add new card (admin only) */}
            {isAdmin && (
              <button
                onClick={addItem}
                className={`rounded-2xl border-2 border-dashed flex flex-col items-center justify-center gap-2 p-6 cursor-pointer transition-all hover:border-pink-400 hover:bg-pink-50/50 min-h-[120px] ${
                  isDark ? "border-gray-600 text-gray-500 hover:text-pink-400" : "border-pink-200 text-gray-400 hover:text-pink-500"
                }`}
              >
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-pink-100">
                  <i className="ri-add-line text-pink-500 text-xl"></i>
                </div>
                <span className="text-sm font-semibold">Add Card</span>
              </button>
            )}
          </div>

          <div className="text-center">
            {(isAdmin || data.closingNote) && (
              <div className={`inline-flex items-center gap-3 rounded-full px-8 py-4 relative group/closing ${isDark ? "bg-gray-800" : ""}`} style={isDark ? {} : { background: "linear-gradient(135deg, #fce7f3 0%, #dcfce7 100%)" }}>
                <i className="ri-checkbox-circle-fill text-green-500 text-xl"></i>
                {isAdmin ? (
                  <AdminEditField value={data.closingNote} onSave={(v) => set("closingNote", v)} onDelete={() => set("closingNote", "")} inputClassName="font-semibold text-base">
                    <span className={`font-semibold text-base ${isDark ? "text-white" : "text-gray-800"}`}>{data.closingNote}</span>
                  </AdminEditField>
                ) : (
                  <span className={`font-semibold text-base ${isDark ? "text-white" : "text-gray-800"}`}>{data.closingNote}</span>
                )}
              </div>
            )}
            {isAdmin && !data.closingNote && (
              <button
                onClick={() => set("closingNote", "All you need to do is stock your space and create. We take care of the rest.")}
                className="inline-flex items-center gap-2 border-2 border-dashed border-pink-200 hover:border-pink-400 text-pink-400 hover:text-pink-500 rounded-full px-6 py-3 text-sm font-semibold transition-colors cursor-pointer"
              >
                <i className="ri-add-line"></i>
                Add closing note
              </button>
            )}
          </div>

          <AppendableBlocks blocks={data.extraBlocks ?? []} onChange={(v) => set("extraBlocks", v)} className="mt-10" />
        </div>
    </section>
  );
}
